import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useApiAccountsV1SignupRequestCreate } from 'api';
import {
  CheckboxField,
  Form,
  type InferType,
  PhoneNumberField,
  phoneNumberValidation,
  TextField,
  useFormWithApiErrors,
  yup,
  yupResolver,
} from 'form';
import { type I18nScopedKey, type I18nTranslateFunction, useTranslation } from 'i18n';
import { useRef } from 'react';
import { Button, useBreakpoint } from 'ui';

import { TermsAndPolicyAgreement } from '../TermsAndPolicyAgreement/TermsAndPolicyAgreement';

type FeaturesTranslate = I18nTranslateFunction<I18nScopedKey<'features'>>;

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const getSchema = (t: FeaturesTranslate) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t('auth.userSignUpForm.validation.invalidEmail'))
      .required(t('auth.userSignUpForm.validation.emailRequired')),
    full_name: yup.string().required(t('auth.userSignUpForm.validation.fullNameRequired')),
    phone_number: phoneNumberValidation().required(t('auth.userSignUpForm.validation.phoneNumberRequired')),
    terms: yup.bool().required(t('auth.userSignUpForm.validation.termsRequired')),
  });

type FormValues = InferType<ReturnType<typeof getSchema>>;
export const UserSignUpForm = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();
  const schema = getSchema(t);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const form = useFormWithApiErrors({
    resolver: yupResolver(schema),
  });

  const { mutate, isPending } = useApiAccountsV1SignupRequestCreate({
    mutation: {
      onError(error) {
        form.setApiErrors(error, { errorMessage: t('auth.userSignUpForm.notifications.errorSigningUp') });
        turnstileRef.current?.reset();
      },
      onSuccess() {
        onSuccess?.();
      },
    },
  });

  const onSubmit = (data: FormValues) => {
    const turnstileToken = turnstileRef.current?.getResponse() ?? '';
    mutate({
      data: {
        email: data.email,
        full_name: data.full_name,
        phone_number: data.phone_number,
        ...(turnstileToken && { turnstile_token: turnstileToken }),
      } as Parameters<typeof mutate>[0]['data'],
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit} data-testid="user-sign-up-form">
      <TextField
        type="text"
        name="full_name"
        size="medium"
        label={t('auth.userSignUpForm.labels.fullName')}
        required
        autoFocus
        fullWidth
      />
      <TextField
        type="text"
        name="email"
        size="medium"
        label={t('auth.userSignUpForm.labels.email')}
        required
        fullWidth
      />
      <PhoneNumberField
        withFlags
        type="text"
        name="phone_number"
        size="medium"
        label={t('auth.userSignUpForm.labels.phoneNumber')}
        required
        fullWidth
      />
      <CheckboxField name="terms" size={isMobile ? 'medium' : 'large'} required label={<TermsAndPolicyAgreement />} />

      {TURNSTILE_SITE_KEY ? (
        <Turnstile ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} options={{ size: 'flexible' }} />
      ) : null}

      <Button type="submit" size="large" fullWidth={true} loading={isPending}>
        {t('auth.userSignUpForm.actions.next')}
      </Button>
    </Form>
  );
};
